import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCompetitors } from '@/hooks/useCompetitors';
import { useInstagramProfiles } from '@/hooks/useInstagramProfiles';
import { supabase } from '@/integrations/supabase/client';
import { CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';

type TimeRange = 'week' | 'month';

interface ChartDataPoint {
  date: string;
  [key: string]: number | string | undefined;
}

interface CompetitorGrowthChartProps {
  timeRange?: TimeRange;
}

const USER_LINE_COLOR = 'hsl(30, 100%, 50%)';
const CHART_COLORS = [
  'hsl(142, 71%, 45%)',
  'hsl(250, 70%, 60%)',
  'hsl(200, 90%, 50%)',
  'hsl(340, 82%, 52%)',
  'hsl(45, 93%, 47%)',
];

export const CompetitorGrowthChart = ({ timeRange = 'week' }: CompetitorGrowthChartProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { profiles } = useInstagramProfiles(user?.id || null);
  const { competitors } = useCompetitors();
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [profileNames, setProfileNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [selectedPoint, setSelectedPoint] = useState<ChartDataPoint | null>(null);
  const [showPostMarkers, setShowPostMarkers] = useState(true);
  const [competitorDataCounts, setCompetitorDataCounts] = useState<Record<string, number>>({});
  const isMobile = useIsMobile();

  const toggleSeries = (name: string) => {
    setHiddenSeries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const fetchGrowthData = async () => {
      if (!user) return;

      setIsLoading(true);
      try {
        const allProfileData: { username: string; date: Date; followers: number; postsCount: number }[] = [];
        const names: string[] = [];

        const daysToFetch = timeRange === 'week' ? 7 : 30;
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysToFetch - 5);

        // Fetch user's growth data
        if (profiles.length > 0) {
          const userProfile = profiles[0];
          const username = `Ty (${userProfile.username})`;
          names.push(username);

          const { data: userMetrics } = await supabase
            .from('instagram_daily_metrics')
            .select('scraped_at, followers_count, posts_count')
            .ilike('username', userProfile.username)
            .gte('scraped_at', cutoffDate.toISOString())
            .order('scraped_at', { ascending: true });

          if (userMetrics && userMetrics.length > 0) {
            userMetrics.forEach((metric) => {
              allProfileData.push({
                username,
                date: new Date(metric.scraped_at),
                followers: metric.followers_count,
                postsCount: metric.posts_count || 0
              });
            });
          }
        }

        // Fetch competitors' growth data
        const activeCompetitors = competitors.filter(c => c.is_active).slice(0, 4);

        for (const competitor of activeCompetitors) {
          const username = `@${competitor.instagram_username}`;
          names.push(username);

          const { data: compMetrics } = await supabase
            .from('competitor_daily_metrics')
            .select('metric_date, followers_count, posts_count')
            .eq('username', competitor.instagram_username)
            .eq('user_id', user.id)
            .gte('metric_date', cutoffDate.toISOString())
            .order('metric_date', { ascending: true });

          if (compMetrics && compMetrics.length > 0) {
            compMetrics.forEach((metric) => {
              allProfileData.push({
                username,
                date: new Date(metric.metric_date),
                followers: metric.followers_count || 0,
                postsCount: metric.posts_count || 0
              });
            });
          }
        }

        // Track per-profile data point counts (to detect competitors with no data yet)
        const dataCounts: Record<string, number> = {};
        names.forEach(name => {
          const uniqueDates = new Set(
            allProfileData.filter(d => d.username === name).map(d => d.date.toISOString().split('T')[0])
          );
          dataCounts[name] = uniqueDates.size;
        });
        setCompetitorDataCounts(dataCounts);

        // Build chart data
        const baseFollowers: { [key: string]: number } = {};
        const previousDayFollowers: { [key: string]: { [date: string]: number } } = {};
        const dataPoints = new Map<string, ChartDataPoint>();

        // Group data by date per profile
        const dailyFollowers: { [username: string]: { [dateKey: string]: number } } = {};
        const dailyPostsCounts: { [username: string]: { [dateKey: string]: number } } = {};

        names.forEach(name => {
          const profileData = allProfileData
            .filter(d => d.username === name)
            .sort((a, b) => a.date.getTime() - b.date.getTime());

          previousDayFollowers[name] = {};
          dailyFollowers[name] = {};
          dailyPostsCounts[name] = {};

          if (profileData.length > 0) {
            profileData.forEach((d) => {
              const dateKey = d.date.toISOString().split('T')[0];
              dailyFollowers[name][dateKey] = d.followers;
              dailyPostsCounts[name][dateKey] = d.postsCount;
            });

            const sortedDateKeys = Object.keys(dailyFollowers[name]).sort();
            sortedDateKeys.forEach((dateKey, idx) => {
              if (idx > 0) {
                previousDayFollowers[name][dateKey] = dailyFollowers[name][sortedDateKeys[idx - 1]];
              } else {
                previousDayFollowers[name][dateKey] = dailyFollowers[name][dateKey];
              }
            });

            // Find baseline: data closest to period start with 2-day tolerance
            let periodStartDate: Date;
            if (timeRange === 'week') {
              periodStartDate = new Date();
              periodStartDate.setDate(periodStartDate.getDate() - 8);
            } else {
              periodStartDate = new Date();
              periodStartDate.setDate(periodStartDate.getDate() - 30);
            }

            const periodStartTimestamp = periodStartDate.getTime();

            const validBaseKeys = sortedDateKeys.filter(key => {
              const keyTimestamp = new Date(key).getTime();
              return keyTimestamp <= periodStartTimestamp && keyTimestamp >= (periodStartTimestamp - 2 * 24 * 60 * 60 * 1000);
            });

            if (validBaseKeys.length > 0) {
              baseFollowers[name] = dailyFollowers[name][validBaseKeys[validBaseKeys.length - 1]];
            } else {
              baseFollowers[name] = dailyFollowers[name][sortedDateKeys[0]];
            }
          }
        });

        // Calculate growth percentages
        allProfileData.forEach((point) => {
          const dateKey = point.date.toISOString().split('T')[0];
          const displayDate = timeRange === 'week'
            ? point.date.toLocaleDateString('pl-PL', { weekday: 'short' })
            : point.date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });

          if (!dataPoints.has(dateKey)) {
            dataPoints.set(dateKey, { date: displayDate, sortKey: dateKey });
          }

          const base = baseFollowers[point.username] || 1;
          const growthPercent = ((point.followers - base) / base) * 100;
          const prevDayFollowersVal = previousDayFollowers[point.username]?.[dateKey] || point.followers;
          const dailyChange = point.followers - prevDayFollowersVal;

          // Calculate posts added (positive delta only)
          const sortedDates = Object.keys(dailyPostsCounts[point.username] || {}).sort();
          const dateIdx = sortedDates.indexOf(dateKey);
          const prevPostsCount = dateIdx > 0 ? (dailyPostsCounts[point.username]?.[sortedDates[dateIdx - 1]] ?? point.postsCount) : point.postsCount;
          const postsAdded = Math.max(0, point.postsCount - prevPostsCount);

          dataPoints.get(dateKey)![point.username] = Number(growthPercent.toFixed(2));
          dataPoints.get(dateKey)![`${point.username}_followers`] = point.followers;
          dataPoints.get(dateKey)![`${point.username}_daily`] = dailyChange;
          dataPoints.get(dateKey)![`${point.username}_postsAdded`] = postsAdded;
        });

        // Sort by date
        let chartArray = Array.from(dataPoints.values()).sort((a, b) => {
          return (a.sortKey as string).localeCompare(b.sortKey as string);
        });

        // Filter to show only the requested period
        if (timeRange === 'week') {
          const baselineDate = new Date();
          baselineDate.setDate(baselineDate.getDate() - 8);
          const baselineKey = baselineDate.toISOString().split('T')[0];

          const filtered = chartArray.filter(item => (item.sortKey as string) >= baselineKey);

          if (!filtered.some(item => (item.sortKey as string) === baselineKey)) {
            // Only add synthetic baseline if real data spans close to it (within 2 days).
            // For new users with few days of data, skip the synthetic point to avoid
            // a misleading gap on the chart (e.g. "sob. → pt. → sob." when only 2 days exist).
            const firstRealKey = filtered.length > 0 ? (filtered[0].sortKey as string) : null;
            const daysDiff = firstRealKey
              ? Math.round((new Date(firstRealKey).getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24))
              : 0;

            if (daysDiff <= 2 || filtered.length === 0) {
              const startPoint: ChartDataPoint = {
                date: baselineDate.toLocaleDateString('pl-PL', { weekday: 'short' }),
                sortKey: baselineKey,
              };
              names.forEach(name => {
                startPoint[name] = 0;
                startPoint[`${name}_followers`] = baseFollowers[name];
                startPoint[`${name}_daily`] = 0;
              });
              filtered.unshift(startPoint);
            } else {
              // Use first real data point as baseline (zero growth starting point)
              const firstPoint = filtered[0];
              names.forEach(name => {
                firstPoint[name] = 0;
                firstPoint[`${name}_daily`] = 0;
              });
            }
          } else {
            const firstPoint = filtered[0];
            names.forEach(name => {
              firstPoint[name] = 0;
              firstPoint[`${name}_daily`] = 0;
            });
          }

          chartArray = filtered;
        } else {
          const displayStartDate = new Date();
          displayStartDate.setDate(displayStartDate.getDate() - 30);
          const displayStartKey = displayStartDate.toISOString().split('T')[0];

          chartArray = chartArray.filter(item => (item.sortKey as string) >= displayStartKey);
        }

        setChartData(chartArray);
        setProfileNames(names);
      } catch (error) {
        console.error('Error fetching growth data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGrowthData();
  }, [user, profiles, competitors, timeRange]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-2xl overflow-hidden border border-border/40 shadow-lg">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </div>
    );
  }

  // Show "collecting data" overlay when:
  // 1. Overall chart has < 2 data points, OR
  // 2. Any active competitor has < 2 days of data (e.g. just added, not scraped yet)
  // Only evaluate competitor counts once they've been populated (Object.keys check)
  // to avoid a flash of the overlay before the first fetch completes.
  const competitorNames = profileNames.filter(name => name.startsWith('@'));
  const countsReady = Object.keys(competitorDataCounts).length > 0;
  const hasCompetitorWithInsufficientData = countsReady && competitorNames.length > 0 &&
    competitorNames.some(name => (competitorDataCounts[name] || 0) < 2);
  const showCollectingDataOverlay = chartData.length < 2 || profileNames.length === 0 || hasCompetitorWithInsufficientData;

  const placeholderChartData = [
    { date: 'sob.', 'Twój profil': 0, '@competitor1': 0 },
    { date: 'niedz.', 'Twój profil': 0.01, '@competitor1': 0.02 },
    { date: 'pon.', 'Twój profil': 0.02, '@competitor1': 0.03 },
    { date: 'wt.', 'Twój profil': 0.03, '@competitor1': 0.035 },
    { date: 'śr.', 'Twój profil': 0.05, '@competitor1': 0.04 },
    { date: 'czw.', 'Twój profil': 0.08, '@competitor1': 0.05 },
    { date: 'pt.', 'Twój profil': 0.12, '@competitor1': 0.06 },
    { date: 'sob.', 'Twój profil': 0.15, '@competitor1': 0.07 },
  ];

  if (showCollectingDataOverlay) {
    return (
      <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-2xl overflow-hidden border border-border/40 shadow-lg relative">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shadow-sm">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                {t('competitors.growthComparison')}
              </CardTitle>
              <CardDescription className="mt-0.5 text-muted-foreground/80">
                {t('competitors.followerGrowth')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2 relative">
          {/* Blurred placeholder chart */}
          <div className="blur-[6px] opacity-50 pointer-events-none select-none">
            <div className="flex flex-wrap gap-4 justify-center py-3 px-4 bg-muted/30 rounded-xl mb-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-background/60 rounded-full border border-border/30">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: USER_LINE_COLOR }} />
                <span className="text-sm font-medium text-foreground">Twój profil</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-background/60 rounded-full border border-border/30">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[0] }} />
                <span className="text-sm font-medium text-foreground">@competitor1</span>
              </div>
            </div>
            <div className="bg-gradient-to-b from-transparent to-muted/10 rounded-xl p-2">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={placeholderChartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                  <defs>
                    <linearGradient id="placeholderGradient1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={USER_LINE_COLOR} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={USER_LINE_COLOR} stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="placeholderGradient2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={CHART_COLORS[0]} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.1} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Area type="monotone" dataKey="Twój profil" stroke={USER_LINE_COLOR} strokeWidth={2} fill="url(#placeholderGradient1)" />
                  <Area type="monotone" dataKey="@competitor1" stroke={CHART_COLORS[0]} strokeWidth={2} fill="url(#placeholderGradient2)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Overlay with message */}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/40 backdrop-blur-[2px] rounded-xl">
            <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-3">
              <TrendingUp className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1.5">
              {t('competitors.collectingData')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md text-center px-4">
              {t('competitors.chartNotReady')}
            </p>
          </div>
        </CardContent>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-background via-background to-muted/20 rounded-2xl overflow-visible border border-border/40 shadow-lg">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shadow-sm">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                {t('competitors.growthComparison')}
              </CardTitle>
              <CardDescription className="mt-0.5 text-muted-foreground/80">
                {t('competitors.followerGrowth')}
              </CardDescription>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className={`text-xs font-medium transition-colors duration-200 ${showPostMarkers ? 'text-purple-600' : 'text-muted-foreground'}`}>
              Posty
            </span>
            <button
              role="switch"
              aria-checked={showPostMarkers}
              onClick={() => setShowPostMarkers(prev => !prev)}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                showPostMarkers ? 'bg-purple-500' : 'bg-muted-foreground/25'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  showPostMarkers ? 'translate-x-[18px]' : 'translate-x-[3px]'
                }`}
              />
            </button>
          </label>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-4">
          {/* Interactive legend */}
          <div className="flex flex-wrap gap-2 justify-center py-3 px-4 bg-muted/30 rounded-xl">
            <span className="w-full text-center text-xs text-muted-foreground mb-1">
              Kliknij, aby pokazać/ukryć
            </span>
            {profileNames.map((name, index) => {
              const isCurrentUser = name.startsWith('Ty');
              const color = isCurrentUser ? USER_LINE_COLOR : CHART_COLORS[index % CHART_COLORS.length];
              const isHidden = hiddenSeries.has(name);
              return (
                <button
                  key={name}
                  onClick={() => toggleSeries(name)}
                  className={`group flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all duration-200 select-none ${
                    isHidden
                      ? 'bg-muted/50 border-border/40 hover:bg-muted/70 hover:border-border/60'
                      : 'bg-background border-border/50 shadow-sm hover:shadow-lg hover:scale-105 hover:border-primary/30'
                  }`}
                  style={{
                    borderColor: isHidden ? undefined : `${color}30`,
                  }}
                >
                  <div
                    className={`w-3 h-3 rounded-full transition-all duration-200 group-hover:scale-110 ${isHidden ? 'opacity-40' : ''}`}
                    style={{
                      backgroundColor: color,
                      boxShadow: isHidden ? 'none' : `0 0 10px ${color}50`
                    }}
                  />
                  <span className={`text-sm font-medium transition-all duration-200 ${
                    isHidden ? 'text-muted-foreground line-through' : 'text-foreground'
                  }`}>
                    {name}
                  </span>
                  {isHidden && (
                    <span className="text-xs text-muted-foreground/70 ml-1">(ukryty)</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Chart */}
          <div className="bg-gradient-to-b from-transparent to-muted/10 rounded-xl p-2">
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                <defs>
                  <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={USER_LINE_COLOR} stopOpacity={0.4} />
                    <stop offset="50%" stopColor={USER_LINE_COLOR} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={USER_LINE_COLOR} stopOpacity={0.02} />
                  </linearGradient>
                  {CHART_COLORS.map((color, index) => (
                    <linearGradient key={`gradient-${index}`} id={`competitorGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="50%" stopColor={color} stopOpacity={0.1} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  strokeOpacity={0.1}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11, fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  interval={timeRange === 'week' ? 0 : 'preserveStartEnd'}
                  tickCount={timeRange === 'week' ? undefined : 6}
                  angle={timeRange === 'month' ? -35 : 0}
                  textAnchor={timeRange === 'month' ? 'end' : 'middle'}
                  height={timeRange === 'month' ? 70 : 30}
                  tickFormatter={(value: string) => {
                    if (!value || value === '') return '';
                    return value;
                  }}
                />
                <YAxis
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'Wzrost %', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))', fontSize: 11, fontWeight: 500 } }}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                  position={isMobile ? undefined : { x: undefined, y: -20 }}
                  content={({ active, payload, coordinate }) => {
                    if (isMobile && active && payload && payload.length > 0) {
                      const data = payload[0]?.payload;
                      if (data && data !== selectedPoint) {
                        setTimeout(() => setSelectedPoint(data), 0);
                      }
                      return (
                        <div className="rounded-full bg-primary/90 text-primary-foreground px-2 py-0.5 text-[10px] font-medium shadow-lg">
                          {data?.date}
                        </div>
                      );
                    }

                    if (!active || !payload || payload.length === 0) return null;
                    const data = payload[0]?.payload;
                    const currentPointIndex = chartData.findIndex(d => d.sortKey === data?.sortKey);

                    const tooltipWidth = 220;
                    const shouldFlipLeft = coordinate && coordinate.x > 600;

                    return (
                      <div
                        className="rounded-xl border border-border/50 bg-background/95 backdrop-blur-md p-4 shadow-xl min-w-[200px]"
                        style={{
                          transform: shouldFlipLeft ? `translateX(-${tooltipWidth}px)` : 'none'
                        }}
                      >
                        <p className="text-sm font-semibold mb-3 pb-2 border-b border-border/30">{data?.date}</p>
                        <div className="space-y-3">
                          {payload.map((entry, index) => {
                            const name = entry.name as string;
                            const followers = data?.[`${name}_followers`] as number | undefined;
                            const dailyChange = data?.[`${name}_daily`] as number | undefined;

                            const currentFollowers = followers || 0;

                            let totalPeriodChange = 0;
                            for (let i = 1; i <= currentPointIndex; i++) {
                              const pointDailyChange = chartData[i]?.[`${name}_daily`] as number | undefined;
                              totalPeriodChange += pointDailyChange || 0;
                            }

                            const baseFollowersValue = currentFollowers - totalPeriodChange;
                            const cumulativeGrowthPercent = baseFollowersValue > 0
                              ? (totalPeriodChange / baseFollowersValue) * 100
                              : 0;

                            return (
                              <div key={index} className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="font-medium text-xs truncate">{name}</span>
                                </div>
                                <div className="pl-5 space-y-0.5 text-xs">
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Obserwatorzy:</span>
                                    <span className="font-medium">{followers?.toLocaleString('pl-PL') || '-'}</span>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Dzienny wzrost:</span>
                                    <span className={`font-medium ${dailyChange && dailyChange > 0 ? 'text-green-500' : dailyChange && dailyChange < 0 ? 'text-red-500' : ''}`}>
                                      {dailyChange !== undefined ? (dailyChange >= 0 ? '+' : '') + dailyChange.toLocaleString('pl-PL') : '-'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Całkowity wzrost:</span>
                                    <span className={`font-medium ${cumulativeGrowthPercent > 0 ? 'text-green-500' : cumulativeGrowthPercent < 0 ? 'text-red-500' : ''}`}>
                                      {`${cumulativeGrowthPercent >= 0 ? '+' : ''}${cumulativeGrowthPercent.toFixed(2)}%`}
                                    </span>
                                  </div>
                                  {showPostMarkers && (() => {
                                    const postsAdded = data?.[`${name}_postsAdded`] as number | undefined;
                                    if (postsAdded && postsAdded > 0) {
                                      return (
                                        <div className="flex justify-between gap-2">
                                          <span className="text-muted-foreground">Nowe posty:</span>
                                          <span className="font-medium text-purple-500">+{postsAdded}</span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }}
                />
                {profileNames.map((name, index) => {
                  const isCurrentUser = name.startsWith('Ty');
                  const color = isCurrentUser ? USER_LINE_COLOR : CHART_COLORS[index % CHART_COLORS.length];
                  const gradientId = isCurrentUser ? 'userGradient' : `competitorGradient${index % CHART_COLORS.length}`;
                  const isHidden = hiddenSeries.has(name);

                  if (isHidden) return null;

                  return (
                    <Area
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={color}
                      strokeWidth={isCurrentUser ? 2.5 : 2}
                      fill={`url(#${gradientId})`}
                      activeDot={{
                        r: isCurrentUser ? 6 : 5,
                        strokeWidth: 2,
                        stroke: 'hsl(var(--background))',
                        fill: color,
                        style: { filter: `drop-shadow(0 0 4px ${color}60)` }
                      }}
                      dot={showPostMarkers ? (props: any) => {
                        const { cx, cy, payload, index: dotIndex } = props;
                        const postsAdded = payload?.[`${name}_postsAdded`] as number;
                        if (dotIndex === 0 || !postsAdded || postsAdded <= 0) return <circle key={`empty-${name}-${dotIndex}`} r={0} />;
                        return (
                          <g key={`post-${name}-${payload?.sortKey}`}>
                            <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={1.5} opacity={0.9} />
                            {postsAdded > 1 && (
                              <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
                                    fill="#fff" fontSize={7} fontWeight="bold">
                                {postsAdded}
                              </text>
                            )}
                          </g>
                        );
                      } : false}
                      connectNulls
                      animationDuration={300}
                    />
                  );
                })}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Mobile data panel */}
          {isMobile && selectedPoint && (
            <div className="mt-3 p-3 bg-muted/30 rounded-xl border border-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{selectedPoint.date}</span>
                <button
                  onClick={() => setSelectedPoint(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {profileNames.filter(name => !hiddenSeries.has(name)).map((name, index) => {
                  const isCurrentUser = name.startsWith('Ty');
                  const color = isCurrentUser ? USER_LINE_COLOR : CHART_COLORS[index % CHART_COLORS.length];
                  const followers = selectedPoint[`${name}_followers`] as number | undefined;
                  const dailyChange = selectedPoint[`${name}_daily`] as number | undefined;
                  const growth = selectedPoint[name] as number | undefined;
                  const postsAdded = selectedPoint[`${name}_postsAdded`] as number | undefined;

                  return (
                    <div
                      key={name}
                      className="p-2 bg-background/60 rounded-lg border border-border/20"
                      style={{ borderLeftColor: color, borderLeftWidth: 3 }}
                    >
                      <div className="text-[10px] font-medium truncate mb-1">{name}</div>
                      <div className="text-xs text-muted-foreground">
                        {followers?.toLocaleString('pl-PL') || '-'}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-medium ${dailyChange && dailyChange > 0 ? 'text-green-500' : dailyChange && dailyChange < 0 ? 'text-red-500' : ''}`}>
                          {dailyChange !== undefined ? (dailyChange >= 0 ? '+' : '') + dailyChange : '-'}
                        </span>
                        <span className={`text-[10px] ${growth && growth > 0 ? 'text-green-500' : growth && growth < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                          ({growth !== undefined ? `${growth >= 0 ? '+' : ''}${growth.toFixed(2)}%` : '-'})
                        </span>
                      </div>
                      {showPostMarkers && postsAdded && postsAdded > 0 && (
                        <div className="text-[10px] text-purple-500 font-medium mt-1">
                          +{postsAdded} {postsAdded === 1 ? 'post' : 'posty'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile hint */}
          {isMobile && !selectedPoint && (
            <div className="mt-2 text-center text-xs text-muted-foreground">
              Dotknij punkt na wykresie, aby zobaczyć szczegóły
            </div>
          )}
        </div>
      </CardContent>
    </div>
  );
};
